import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2, UserPlus, Crown, Shield, User, Trash2 } from 'lucide-react';
import { WorkspaceMember } from '../../lib/api/workspaceApi';

interface WorkspaceMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceName: string;
  members: WorkspaceMember[];
  currentUserRole: 'owner' | 'admin' | 'member';
  onAddMember: (email: string, role: 'admin' | 'member') => Promise<void>;
  onRemoveMember?: (userId: string) => Promise<void>;
  isLoading?: boolean;
}

const roleIcons = {
  owner: Crown,
  admin: Shield,
  member: User,
};

const roleLabels = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

export function WorkspaceMembersDialog({
  open,
  onOpenChange,
  workspaceName,
  members,
  currentUserRole,
  onAddMember,
  onRemoveMember,
  isLoading = false,
}: WorkspaceMembersDialogProps) {
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'admin' | 'member'>('member');
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  useEffect(() => {
    if (open) {
      setNewMemberEmail('');
      setNewMemberRole('member');
      setError(null);
    }
  }, [open]);

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemberEmail.trim()) return;

    setIsAdding(true);
    setError(null);

    try {
      await onAddMember(newMemberEmail.trim(), newMemberRole);
      setNewMemberEmail('');
      setNewMemberRole('member');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-card border border-border rounded-lg shadow-lg z-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">Workspace Members</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                {workspaceName}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent">
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Add Member Form */}
          {canManageMembers && (
            <form onSubmit={handleAddMember} className="mb-4">
              <p className="text-sm font-medium mb-2">Add Member</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="Email address"
                  className="flex-1 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value as 'admin' | 'member')}
                  className="px-3 py-2 text-sm bg-background border border-input rounded-md"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  type="submit"
                  disabled={!newMemberEmail.trim() || isAdding}
                  className="px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {isAdding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                </button>
              </div>
              {error && <p className="text-sm text-destructive mt-1">{error}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                The user must already have an account. Enter their email address.
              </p>
            </form>
          )}

          {/* Members List */}
          <div className="border border-border rounded-md overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No members yet</p>
            ) : (
              <div className="divide-y divide-border">
                {members.map((member) => {
                  const Icon = roleIcons[member.role];
                  return (
                    <div
                      key={member.userId}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30"
                    >
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {member.name || member.email || member.userId}
                        </p>
                        {member.email && member.name && (
                          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                        {roleLabels[member.role]}
                      </span>
                      {canManageMembers &&
                        member.role !== 'owner' &&
                        onRemoveMember && (
                          <button
                            onClick={() => onRemoveMember(member.userId)}
                            className="p-1 rounded hover:bg-destructive/10 hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove member"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4">
            <Dialog.Close asChild>
              <button className="px-4 py-2 text-sm bg-muted hover:bg-accent rounded-md">
                Close
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default WorkspaceMembersDialog;
